// src/components/cards/shared/dialogs/ConvertQuoteDialog.tsx
//
// Orchestrator for the convert-quote-to-booking dialog. Branches on
// clientIsProspect: false renders the simple rename-only body
// (existing-client path, unchanged); true renders
// ConvertQuoteProspectBody, which builds the extended
// ConvertQuoteRequest (clientPatch, kennels, extended pets).
//
// Mobile + prospect path goes full-screen (DialogBase.fullScreen) for
// the long form; desktop/simple path use a centered dialog.

import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import DialogBase from '../../../common/modal/DialogBase';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import LoadingSpinner from '../../../common/LoadingSpinner';
import { getCustomIcon } from '../../../../utils/customIcons';
import { StyledTextField } from '../../../forms/shared';
import { ConvertQuoteRequest } from '../../../../services/api';
import ConvertQuoteProspectBody, {
  ConvertProspectSketchPet,
  ConvertQuoteProspectBodyHandle,
} from './convert/ConvertQuoteProspectBody';

export interface ConvertQuoteSketchPet {
  petId: number;
  petName: string;
  petType: string;
  petBreed: string;
}

// "8 June 2026" style, matching QuoteDetailCard's formatDate.
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

interface ConvertQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  // Simple path emits { pets: [{ petId, name }] }; prospect path emits
  // the extended payload (pets, clientPatch, kennels).
  onConfirm: (payload: ConvertQuoteRequest) => void | Promise<void>;
  sketchPets: ConvertQuoteSketchPet[];
  clientName: string;
  clientIsProspect: boolean;
  submitting: boolean;
  // Prospect-only context, required when clientIsProspect=true.
  clientId?: number;
  checkInDate?: string;
  checkOutDate?: string;
  numberOfNights?: number;
}

const ConvertQuoteDialog: React.FC<ConvertQuoteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  sketchPets,
  clientName,
  clientIsProspect,
  submitting,
  clientId,
  checkInDate,
  checkOutDate,
  numberOfNights,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const useFullScreen = isMobile && clientIsProspect;

  // --- Simple-path state (existing client) ---------------------------
  const [names, setNames] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!open) return;
    if (clientIsProspect) return;
    const initial: Record<number, string> = {};
    sketchPets.forEach((p) => {
      initial[p.petId] = p.petName || '';
    });
    setNames(initial);
    setErrors({});
  }, [open, clientIsProspect, sketchPets]);

  const handleSimpleNameChange = (petId: number, value: string) => {
    setNames((prev) => ({ ...prev, [petId]: value }));
    if (errors[petId]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[petId];
        return next;
      });
    }
  };

  const handleSimpleConfirm = () => {
    const nextErrors: Record<number, string> = {};
    sketchPets.forEach((p) => {
      const trimmed = (names[p.petId] || '').trim();
      if (!trimmed) nextErrors[p.petId] = 'Name required.';
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    onConfirm({
      pets: sketchPets.map((p) => ({
        petId: p.petId,
        name: (names[p.petId] || '').trim(),
      })),
    });
  };

  // Triggers ConvertQuoteProspectBody's internal validate+submit via a ref
  // (scoped to this exact instance - keeps the parent agnostic to its
  // state shape without a global window event that any live instance
  // could pick up).
  const prospectBodyRef = useRef<ConvertQuoteProspectBodyHandle>(null);
  const handleProspectConfirm = () => {
    prospectBodyRef.current?.submit();
  };

  const handleProspectPayload = (payload: ConvertQuoteRequest) => {
    onConfirm(payload);
  };

  const handleConfirmClick = () => {
    if (clientIsProspect) handleProspectConfirm();
    else handleSimpleConfirm();
  };

  // Enabled whenever not submitting; validation runs on click for both
  // paths (prospect path scrolls to the first invalid section).
  const convertDisabled = submitting;

  return (
    <DialogBase
      draggable
      open={open}
      onClose={onClose}
      title="Convert to Booking"
      headerIcon={getCustomIcon('convert', '2.6rem')}
      headerColor="primary"
      fullScreen={useFullScreen}
      maxWidth={clientIsProspect && !useFullScreen ? 'md' : undefined}
      contentSx={clientIsProspect ? { padding: 0 } : undefined}
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            disabled={submitting}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': {
                display: { xs: 'none', sm: 'flex' },
              },
            }}
          />
          <ModalSaveButton
            onClick={handleConfirmClick}
            disabled={convertDisabled}
            text={submitting ? 'Converting...' : 'Convert'}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': {
                display: { xs: 'none', sm: 'flex' },
              },
              '&.Mui-disabled': {
                backgroundColor: 'primary.main',
                color: 'white',
                opacity: 0.5,
              },
            }}
          />
        </>
      }
    >
      <Box
        sx={{
          position: 'relative',
          ...(clientIsProspect && !useFullScreen
            ? { maxHeight: '70vh', overflowY: 'auto' }
            : {}),
          // Mobile prospect path: match FieldLabelContainer's mobile
          // background so label/value cells appear flat (FormWrapper
          // pattern from NewClient/NewQuote/BookingForm).
          ...(clientIsProspect && {
            [theme.breakpoints.down('sm')]: {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[750]
                  : theme.palette.grey[300],
            },
          }),
        }}
      >
        {submitting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: (t) =>
                t.palette.mode === 'dark'
                  ? 'rgba(0,0,0,0.55)'
                  : 'rgba(255,255,255,0.75)',
            }}
          >
            <LoadingSpinner compact size={80} message="" />
          </Box>
        )}

        {/* Uses component="div" to avoid "<p> cannot contain <div>"
            with nested Typography. */}
        <Box
          sx={{
            ...(clientIsProspect ? { px: 2, pt: 1.5, pb: 1 } : { pt: 1 }),
          }}
        >
          <Typography component="div" sx={{ fontSize: '2rem', mb: 0.1 }}>
            {!clientIsProspect && (
              <Typography
                component="div"
                sx={{
                  borderBottom: (t) =>
                    `3px dashed ${
                      (t.palette as { dividerContrast?: string })
                        .dividerContrast ?? t.palette.divider
                    }`,
                  fontSize: '1.8rem',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                Convert quote to booking for:
              </Typography>
            )}
            <Box component="strong">{clientName}</Box>
          </Typography>

          {/* Quote dates + stay length, stacked, no labels. */}
          {clientIsProspect && checkInDate && checkOutDate && (
            <Box sx={{ mt: -0.25, mb: 0.5 }}>
              <Typography
                component="div"
                sx={{
                  fontSize: '1.8rem',
                  color: 'text.main',
                  whiteSpace: 'nowrap',
                  overflowX: 'auto',
                  overflowY: 'visible',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {formatDate(checkInDate)} - {formatDate(checkOutDate)}
              </Typography>
              {typeof numberOfNights === 'number' && (
                <Typography
                  component="div"
                  sx={{
                    fontSize: '1.8rem',
                    color: 'text.main',
                    paddingBottom: '1rem',
                    borderBottom: (t) =>
                      `3px dashed ${
                        (t.palette as { dividerContrast?: string })
                          .dividerContrast ?? t.palette.divider
                      }`,
                  }}
                >
                  {numberOfNights} night{numberOfNights !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Body branch */}
        {clientIsProspect ? (
          clientId && checkInDate && checkOutDate && numberOfNights ? (
            <ConvertQuoteProspectBody
              ref={prospectBodyRef}
              open={open}
              clientId={clientId}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              numberOfNights={numberOfNights}
              sketchPets={sketchPets as ConvertProspectSketchPet[]}
              isSubmitting={submitting}
              onConfirm={handleProspectPayload}
            />
          ) : (
            <Box
              sx={{ py: 4, px: 2, textAlign: 'center', color: 'error.main' }}
            >
              Missing quote context. Please reload the page and try again.
            </Box>
          )
        ) : (
          sketchPets.length > 0 && (
            <>
              <Typography
                component="div"
                sx={{
                  fontSize: '1.8rem',
                  color: 'text.main',
                  mb: 1.5,
                }}
              >
                Rename pets..?
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {sketchPets.map((pet) => (
                  <Box
                    key={pet.petId}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '2.5rem 1fr',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ textAlign: 'center', pt: '.4rem' }}>
                      {getCustomIcon(pet.petType, '2.6rem')}
                    </Box>
                    <StyledTextField
                      fullWidth
                      placeholder="Pet name"
                      value={names[pet.petId] ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleSimpleNameChange(pet.petId, e.target.value)
                      }
                      error={!!errors[pet.petId]}
                      helperText={errors[pet.petId]}
                      disabled={submitting}
                    />
                  </Box>
                ))}
              </Box>
            </>
          )
        )}
      </Box>
    </DialogBase>
  );
};

export default ConvertQuoteDialog;
